'use client';
import React, { useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import StatusBadge from '@/components/ui/StatusBadge';
import ConfirmModal from '@/components/ui/ConfirmModal';
import { Edit, Eye, Trash2, Plus, Camera, Flame } from 'lucide-react';
import { toast } from 'sonner';
import Link from 'next/link';

const INITIAL_DOGS = [
{
  id: 'dog-001',
  name: 'Biscuit',
  breed: 'Golden Retriever',
  dob: 'May 3, 2019',
  status: 'active' as const,
  statusLabel: 'Active',
  paws: 2847,
  memories: 47,
  certificates: 6,
  achievements: 18,
  followers: 1891,
  completeness: 96,
  isFoundingDog: true,
  photo: "https://img.rocket.new/generatedImages/rocket_gen_img_1f2f1fc04-1772154247550.png",
  photoAlt: 'Golden retriever Biscuit smiling with tongue out in park',
  about: 'The sweetest golden girl who loves the beach, agility class, and stealing socks.'
},
{
  id: 'dog-002',
  name: 'Mochi',
  breed: 'Shiba Inu',
  dob: 'Aug 17, 2023',
  status: 'active' as const,
  statusLabel: 'Active',
  paws: 834,
  memories: 18,
  certificates: 2,
  achievements: 5,
  followers: 412,
  completeness: 72,
  isFoundingDog: false,
  photo: "https://images.unsplash.com/photo-1591006195282-52438123024b",
  photoAlt: 'Shiba Inu Mochi sitting alert with pointed ears',
  about: 'Mochi is a dramatic, extremely opinionated Shiba who believes he is the main character.'
},
{
  id: 'dog-003',
  name: 'Duchess',
  breed: 'Border Collie Mix',
  dob: 'Oct 12, 2015',
  status: 'memorial' as const,
  statusLabel: 'Memorial',
  paws: 1204,
  memories: 63,
  certificates: 4,
  achievements: 11,
  followers: 981,
  completeness: 88,
  isFoundingDog: false,
  photo: "https://images.unsplash.com/photo-1566563182302-0ae18672f504",
  photoAlt: 'Border collie mix Duchess with soft eyes looking at camera lovingly',
  about: 'Forever our girl. Duchess loved herding anything that moved — including us.'
}];


export default function MyDogsGrid() {
  const [dogs, setDogs] = useState(INITIAL_DOGS);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);

  const handleDelete = () => {
    if (!deleteTarget) return;
    setDogs((d) => d.filter((dog) => dog.id !== deleteTarget));
    setDeleteTarget(null);
    toast.success('Dog profile removed.');
  };

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-3 gap-6">
        {dogs.map((dog) =>
        <div key={dog.id} className="bg-card border border-border rounded-2xl overflow-hidden shadow-card card-hover group">
            {/* Photo */}
            <div className="relative h-52 bg-muted overflow-hidden">
              <AppImage src={dog.photo} alt={dog.photoAlt} fill className="object-cover group-hover:scale-105 transition-transform duration-500" sizes="(max-width: 768px) 100vw, 33vw" />
              <div className="absolute inset-0 bg-gradient-to-t from-foreground/50 to-transparent" />
              {/* Status overlay */}
              {dog.status === 'memorial' &&
            <div className="absolute inset-0 bg-purple-900/20" />
            }
              {/* Badges */}
              <div className="absolute top-3 left-3 flex gap-2">
                <StatusBadge variant={dog.status} label={dog.statusLabel} />
                {dog.isFoundingDog &&
              <span className="bg-amber-500 text-white text-xs font-700 px-2 py-0.5 rounded-full">🏅 Founding</span>
              }
              </div>
              {/* Completeness */}
              <div className="absolute top-3 right-3">
                <div className="bg-card/90 backdrop-blur-sm rounded-full px-2 py-0.5 flex items-center gap-1">
                  <div className="w-12 h-1.5 bg-border rounded-full overflow-hidden">
                    <div className="h-full bg-success rounded-full" style={{ width: `${dog.completeness}%` }} />
                  </div>
                  <span className="text-xs font-600 text-foreground">{dog.completeness}%</span>
                </div>
              </div>
              {/* Name overlay */}
              <div className="absolute bottom-3 left-4 right-4">
                <h3 className="text-xl font-800 text-white">{dog.name}</h3>
                <p className="text-white/80 text-sm font-500">{dog.breed}</p>
              </div>
              {/* Action overlay */}
              <div className="absolute inset-0 bg-foreground/10 opacity-0 group-hover:opacity-100 transition-opacity flex items-start justify-end p-3 gap-2">
                <Link href="/" className="w-8 h-8 bg-white/90 rounded-lg flex items-center justify-center text-foreground hover:bg-white transition-colors" title="View public profile">
                  <Eye size={14} />
                </Link>
                <button className="w-8 h-8 bg-white/90 rounded-lg flex items-center justify-center text-foreground hover:bg-white transition-colors" title="Edit dog profile">
                  <Edit size={14} />
                </button>
                <button
                onClick={() => setDeleteTarget(dog.id)}
                className="w-8 h-8 bg-white/90 rounded-lg flex items-center justify-center text-danger hover:bg-white transition-colors"
                title="Remove dog profile — this cannot be undone">
                
                  <Trash2 size={14} />
                </button>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-4 divide-x divide-border border-b border-border">
              {[
            { label: 'Paws', val: dog.paws.toLocaleString(), emoji: '🐾' },
            { label: 'Memories', val: dog.memories, emoji: '📅' },
            { label: 'Certs', val: dog.certificates, emoji: '🎖' },
            { label: 'Followers', val: dog.followers.toLocaleString(), emoji: '❤️' }].
            map((s) =>
            <div key={`${dog.id}-stat-${s.label}`} className="flex flex-col items-center py-3 gap-0.5">
                  <span className="text-sm">{s.emoji}</span>
                  <span className="text-sm font-700 text-foreground stat-badge">{s.val}</span>
                  <span className="text-[10px] text-muted-foreground">{s.label}</span>
                </div>
            )}
            </div>

            {/* About + actions */}
            <div className="p-4">
              <p className="text-xs text-muted-foreground leading-relaxed mb-4 line-clamp-2">{dog.about}</p>
              <div className="flex gap-2">
                <button className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-muted rounded-lg text-xs font-600 text-foreground hover:bg-secondary hover:text-primary transition-colors">
                  <Plus size={13} />
                  Add Memory
                </button>
                <button className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-muted rounded-lg text-xs font-600 text-foreground hover:bg-amber-50 hover:text-accent transition-colors">
                  <Camera size={13} />
                  Portrait
                </button>
                {dog.status === 'memorial' &&
              <button className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-purple-50 rounded-lg text-xs font-600 text-purple-700 hover:bg-purple-100 transition-colors">
                    <Flame size={13} />
                    Candle
                  </button>
              }
              </div>
            </div>
          </div>
        )}

        {/* Add dog card */}
        <Link
          href="/my-dogs-management"
          className="border-2 border-dashed border-border rounded-2xl flex flex-col items-center justify-center gap-3 p-8 text-muted-foreground hover:border-primary hover:text-primary hover:bg-secondary/40 transition-all min-h-[340px] group">
          
          <div className="w-16 h-16 rounded-2xl bg-muted group-hover:bg-primary/10 flex items-center justify-center transition-colors">
            <Plus size={28} className="group-hover:text-primary transition-colors" />
          </div>
          <div className="text-center">
            <p className="font-700 text-sm">Add Another Dog</p>
            <p className="text-xs mt-1 text-muted-foreground">Premium · Unlimited profiles</p>
          </div>
        </Link>
      </div>

      <ConfirmModal
        open={!!deleteTarget}
        title="Remove Dog Profile"
        description="This will permanently remove this dog's profile, all memories, certificates, and achievements. This cannot be undone."
        confirmLabel="Remove Profile"
        destructive
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)} />
      
    </>);

}